import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPsap } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import { fetchAgencyLinks } from '@rsos/reduxDataStore/src/features/peerAgencies/peerAgenciesSlice';
import { fetchUser } from '@rsos/sinatra';

const initialState = {
  initializing: true,
  initialized: false,
  initError: null,
};
const hardRedirect = to => {
  window.location = to;
};

// Data loading only. Whether the user may be here is decided by the route's
// <RequireAccess> guard in app/Main.tsx; the catch below handles authentication
// (no session) rather than authorization.
export const initialize = createAsyncThunk(
  'appPeerAgencies/initialize',
  async (args, { dispatch, getState }) => {
    const isLoggedIn = getState().sinatra.user.isLoggedIn;
    try {
      await dispatch(fetchUser());
      // currentOrg is resolved by fetchUser from the user's capstone or
      // central_station role, so it matches the role the access descriptors are
      // evaluated against.
      const orgName = getState().sinatra.user.currentOrg?.name;
      if (!orgName) {
        throw new Error('No admin organization for the current role');
      }
      dispatch(fetchAgencyLinks(orgName));
      // Awaited because this route is gated on the AGENCY_SHARE_CHAT capability,
      // which comes from this PSAP: opening the section's gate first leaves the
      // guard resolving an empty capability set, which reads as "not entitled"
      // and redirects out of admin.
      await dispatch(fetchPsap(`/psaps/${orgName}`)).unwrap();
    } catch (err) {
      if (isLoggedIn) {
        throw err;
      }
      return hardRedirect('/');
    }
  },
);

const appSlice = createSlice({
  name: 'appPeerAgencies',
  initialState,
  extraReducers: builder => {
    builder
      .addCase(initialize.pending, state => {
        state.initializing = true;
        state.initialized = false;
        state.initError = null;
      })
      .addCase(initialize.fulfilled, state => {
        state.initializing = false;
        state.initialized = true;
        state.initError = null;
      })
      .addCase(initialize.rejected, (state, { error = {} }) => {
        state.initializing = false;
        state.initialized = false;
        state.initError = error.message;
      });
  },
});

export const appReducer = appSlice.reducer;
export default appReducer;
